// Keep Fit App - 首頁

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRecords, getStats } from '../services/api';
import type { Nutrition } from '../types';
import { DEFAULT_TARGETS, MEAL_TYPE_LABELS } from '../types';

export default function Home() {
  const today = new Date().toISOString().split('T')[0];
  
  // 獲取今日記錄
  const { data: recordsData } = useQuery({
    queryKey: ['records', today],
    queryFn: () => getRecords({ date: today }),
  });

  // 獲取本週統計
  const { data: statsData } = useQuery({
    queryKey: ['stats', 'week'],
    queryFn: () => getStats('week'),
  });

  const todayRecords = recordsData?.data || [];
  const weekStats = statsData?.data || [];

  // 計算今日營養總計
  const todayTotal: Nutrition = todayRecords.reduce(
    (acc, record) => ({
      calories: acc.calories + (record.calories || 0),
      protein: acc.protein + (record.protein || 0),
      fat: acc.fat + (record.fat || 0),
      carbs: acc.carbs + (record.carbs || 0),
      sugar: acc.sugar + (record.sugar || 0),
      sodium: acc.sodium + (record.sodium || 0),
    }),
    { calories: 0, protein: 0, fat: 0, carbs: 0, sugar: 0, sodium: 0 }
  );

  // 計算達成率
  const getProgress = (current: number, target: number) => {
    const percent = (current / target) * 100;
    return Math.min(percent, 100);
  };

  const ProgressBar = ({ label, current, target, unit, color }: {
    label: string;
    current: number;
    target: number;
    unit: string;
    color: string;
  }) => {
    const percent = getProgress(current, target);
    return (
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{label}</span>
          <span className="font-medium">
            {Math.round(current)} / {target}{unit}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${color} transition-all duration-500`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 快速操作 */}
      <section className="grid grid-cols-2 gap-3">
        <Link
          to="/analyze"
          className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-shadow"
        >
          <div className="text-3xl mb-2">📸</div>
          <div className="font-medium">拍照分析</div>
        </Link>
        <Link
          to="/records"
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-4 text-center shadow-md hover:shadow-lg transition-shadow"
        >
          <div className="text-3xl mb-2">📝</div>
          <div className="font-medium">查看記錄</div>
        </Link>
      </section>

      {/* 今日營養概覽 */}
      <section className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">📊 今日營養</h2>
        
        <ProgressBar
          label="卡路里"
          current={todayTotal.calories}
          target={DEFAULT_TARGETS.calories}
          unit=" kcal"
          color="bg-orange-500"
        />
        <ProgressBar
          label="蛋白質"
          current={todayTotal.protein}
          target={DEFAULT_TARGETS.protein}
          unit="g"
          color="bg-red-500"
        />
        <ProgressBar
          label="脂肪"
          current={todayTotal.fat}
          target={DEFAULT_TARGETS.fat}
          unit="g"
          color="bg-yellow-500"
        />
        <ProgressBar
          label="碳水化合物"
          current={todayTotal.carbs}
          target={DEFAULT_TARGETS.carbs}
          unit="g"
          color="bg-blue-500"
        />
      </section>

      {/* 今日記錄 */}
      <section className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">🍽️ 今日飲食</h2>
          <span className="text-sm text-gray-500">
            {todayRecords.length} 項
          </span>
        </div>

        {todayRecords.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <div className="text-4xl mb-2">🈳</div>
            <p>今日仲未有任何記錄</p>
            <Link to="/analyze" className="text-green-600 hover:underline">
              開始分析食物
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {todayRecords.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {record.mealType === 'breakfast' && '🌅'}
                    {record.mealType === 'lunch' && '☀️'}
                    {record.mealType === 'dinner' && '🌙'}
                    {record.mealType === 'snack' && '🍪'}
                  </span>
                  <div>
                    <div className="font-medium">{record.foodName}</div>
                    <div className="text-sm text-gray-500">
                      {MEAL_TYPE_LABELS[record.mealType]}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-orange-600">
                    {record.calories} kcal
                  </div>
                  <div className="text-xs text-gray-400">
                    P:{record.protein}g F:{record.fat}g C:{record.carbs}g
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 本週趨勢 */}
      <section className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">📈 本週趨勢</h2>
        {weekStats.length > 0 ? (
          <div className="text-center text-gray-500">
            <Link to="/stats" className="text-green-600 hover:underline">
              查看詳細統計 →
            </Link>
          </div>
        ) : (
          <div className="text-center text-gray-400 py-4">
            暫無數據
          </div>
        )}
      </section>
    </div>
  );
}
